/**
 * @file PlanSummaryCard.tsx
 * @brief The "Your plan" card, one layout for the company admin's Billing and
 *        the personal Billing → Plan (plans-visible brief B, 21-10-2026).
 *
 * @details
 * Redesign (DES 21-10-2026, "make this beautiful"): a raised card with the
 * plan's icon, name and state, the page's buttons on the right; three small
 * fact tiles (Seats or Billing, Price, Next bill); then the Intelligence
 * minutes bar. A past-due plan says so under the facts. A Free account shows
 * its plan name and `freeLine`, with no money tiles. Next bill shows the
 * amount and any credit applied (nextBillNote), not only "covered by credit".
 *
 * @author DES
 * @date 21-10-2026
 */

import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Building2, CircleAlert, Sparkles, User } from "lucide-react";
import { InfoTip } from "@/components/ui/InfoTip";
import { GridBand } from "@/features/analytics/components/GridBlock";
import { usePlanCatalog } from "@/features/my-billing/api";
import { formatDay, price } from "@/features/my-billing/components/format";
import { cn } from "@/shared/theme/cn";
import { nextBillNote, type PlanSummary } from "./planSummary";
import { planTips, priceTip } from "./planTips";

function stateBadge(plan: PlanSummary): { label: string; tone: "ok" | "warn" | "bad" } | null {
  if (!plan.live) return null;
  if (plan.pastDue) return { label: "Payment failed", tone: "bad" };
  if (plan.cancelAtPeriodEnd) return { label: "Cancelling", tone: "warn" };
  if (plan.status === "trialing") return { label: "Trial", tone: "ok" };
  return { label: "Active", tone: "ok" };
}

const TONE = {
  ok: "bg-success-soft text-success",
  warn: "bg-warning/15 text-warning",
  bad: "bg-destructive/15 text-destructive",
} as const;

/** One of the three facts under the plan name. */
function Fact({
  label,
  value,
  note,
  noteClass,
  wrap = false,
  tip,
}: {
  label: string;
  /** The (i) text for this fact (planTips.ts). */
  tip?: string;
  value: ReactNode;
  note?: ReactNode;
  noteClass?: string;
  /** Let a long note run onto two lines (Next bill with credit). */
  wrap?: boolean;
}) {
  return (
    <div className="min-w-0 rounded-2xl border border-border/70 bg-background/60 px-4 py-3 dark:bg-white/[0.03]">
      <p className="flex items-center gap-1 text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
        {label}
        {tip ? <InfoTip label={label} text={tip} /> : null}
      </p>
      <p className="mt-1.5 truncate text-xl font-semibold tracking-tight text-foreground">{value}</p>
      {note ? (
        <p className={cn("mt-0.5 text-xs text-muted-foreground", wrap ? "leading-snug" : "truncate", noteClass)}>{note}</p>
      ) : null}
    </div>
  );
}

export function PlanSummaryCard({
  plan,
  freeLine,
  cardsHref,
  actions,
}: {
  plan: PlanSummary;
  /** What a Free account gets (the page's own words). */
  freeLine?: ReactNode;
  /** Where to fix a failed payment. */
  cardsHref: string;
  actions?: ReactNode;
}) {
  const badge = stateBadge(plan);
  const yearly = plan.interval === "year";
  const showUsage = plan.live && plan.intelIncluded != null && plan.intelIncluded > 0;
  const used = plan.intelUsed ?? 0;
  const usedPct = showUsage ? Math.min(100, (used / plan.intelIncluded!) * 100) : 0;
  const Icon = plan.audience === "personal" ? (plan.live ? Sparkles : User) : Building2;
  const bill = nextBillNote(plan, (minor) => price(minor, plan.currency));
  // Tip numbers from the catalogue (cached, shared with Compare plans).
  const catalog = usePlanCatalog();
  const rows = catalog.data?.compare ?? catalog.data?.catalog ?? [];
  const tips = planTips(rows);
  const row = rows.find((p) => p.code === plan.planCode);

  return (
    <GridBand>
      <div className="rounded-[20px] border border-border/70 bg-app-card p-5 md:p-6">
        {/* Head: icon, name, state, the page's buttons. */}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3.5">
            <span
              className={cn(
                "flex size-12 shrink-0 items-center justify-center rounded-2xl",
                plan.live ? "bg-brand/[0.12] text-brand" : "bg-black/[0.05] text-muted-foreground dark:bg-white/[0.06]",
              )}
            >
              <Icon className="size-6" strokeWidth={1.75} />
            </span>
            <div className="min-w-0">
              <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground">Your plan</p>
              <h3 className="mt-0.5 flex flex-wrap items-center gap-2 text-[22px] font-semibold leading-tight tracking-tight">
                {plan.planName}
                {badge ? (
                  <span className={cn("rounded-full px-2.5 py-0.5 text-xs font-medium", TONE[badge.tone])}>
                    {badge.label}
                  </span>
                ) : null}
              </h3>
            </div>
          </div>
          {actions ? <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div> : null}
        </div>

        {!plan.live ? (
          freeLine ? <p className="mt-4 max-w-2xl text-sm leading-relaxed text-muted-foreground">{freeLine}</p> : null
        ) : (
          <>
            {/* Three facts. */}
            <div className="mt-5 grid grid-cols-1 gap-2.5 sm:grid-cols-3">
              {plan.seats != null ? (
                <Fact
                  label="Seats"
                  tip={tips.seats}
                  value={plan.seats}
                  note={`Every member${plan.minSeats ? `, ${plan.minSeats} minimum` : ""}`}
                />
              ) : (
                <Fact
                  label="Billing"
                  tip={tips.billing}
                  value={yearly ? "Yearly" : "Monthly"}
                  note={yearly ? "One payment a year" : "One payment a month"}
                />
              )}
              <Fact
                label="Price"
                tip={row ? priceTip(row) : tips.price}
                value={
                  plan.priceMinor != null ? (
                    <>
                      {price(plan.priceMinor, plan.currency)}
                      <span className="ml-1 text-sm font-normal text-muted-foreground">
                        {plan.seats != null ? "/seat" : ""}/{yearly ? "yr" : "mo"}
                      </span>
                    </>
                  ) : (
                    "Not set"
                  )
                }
                note={`Billed ${yearly ? "yearly" : "monthly"}`}
              />
              {plan.cancelAtPeriodEnd ? (
                <Fact
                  label="Ends"
                  tip={tips.ends}
                  value={formatDay(plan.periodEnd) ?? "End of period"}
                  note="Nothing more is charged"
                  noteClass="text-warning"
                />
              ) : (
                <Fact
                  label="Next bill"
                  tip={tips.nextBill}
                  value={formatDay(plan.nextBillDate) ?? "Not yet known"}
                  note={bill?.text}
                  noteClass={bill?.credit ? "text-success" : undefined}
                  wrap
                />
              )}
            </div>

            {plan.pastDue ? (
              <p className="mt-4 flex flex-wrap items-center gap-1.5 rounded-xl bg-destructive/10 px-3.5 py-2.5 text-sm text-destructive">
                <CircleAlert className="h-4 w-4" />
                The last payment failed, so plan minutes are paused.
                <Link to={cardsHref} className="font-medium underline underline-offset-2">
                  Update the card
                </Link>
              </p>
            ) : null}

            {/* Intelligence minutes. */}
            {showUsage ? (
              <div className="mt-5">
                <div className="flex items-baseline justify-between gap-3 text-sm">
                  <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                    Intelligence minutes this period
                    <InfoTip label="Intelligence minutes" text={tips.intel} />
                  </span>
                  <span className="tabular-nums">
                    <span className="font-semibold text-foreground">{used.toLocaleString("en-GB")}</span>
                    <span className="text-muted-foreground"> / {plan.intelIncluded!.toLocaleString("en-GB")}</span>
                  </span>
                </div>
                <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-black/[0.07] dark:bg-white/[0.08]" aria-hidden>
                  <div
                    className="h-full rounded-full bg-brand transition-[width] duration-500"
                    style={{ width: `${used > 0 ? Math.max(2, usedPct) : 0}%` }}
                  />
                </div>
                <p className="mt-1.5 flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
                  {plan.seats != null ? (
                    <>
                      Shared by the whole team
                      <InfoTip label="Shared by the whole team" text={tips.pooled} />
                    </>
                  ) : (
                    "Included with your plan"
                  )}
                  {plan.periodEnd ? ` · resets ${formatDay(plan.periodEnd)}` : ""}
                </p>
              </div>
            ) : null}
          </>
        )}
      </div>
    </GridBand>
  );
}
